import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useModal } from '../hooks/useModal';
import { useCrud } from '../hooks/useCrud';
import { useAsideContext } from '@contexts/AsideContext';
import { PageMeta, TableCard, ColumnDefinition } from '@common/components';
import { Button, Badge, ConfirmDialog } from '@common/components/ui';
import promotionService, { Promotion } from '@services/promotionService';
import PromotionForm from '@components/promotions/PromotionForm';

import { BadgeColor } from '@common/components/ui/Badge';

const typeBadgeColors: Record<string, BadgeColor> = {
  PERCENTAGE: 'info',
  FIXED_AMOUNT: 'success',
  FREE_SHIPPING: 'purple',
};

const statusBadgeColors: Record<string, BadgeColor> = {
  active: 'success',
  expired: 'error',
  scheduled: 'warning',
  inactive: 'gray',
};

function formatDate(iso?: string) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString();
}

function formatValue(p: Promotion) {
  if (p.type === 'FREE_SHIPPING') return 'Free';
  if (p.type === 'PERCENTAGE') return `${p.value}%`;
  return `€${p.value}`;
}

const Promotions = () => {
  const { openAside, closeAside } = useAsideContext();
  const { isOpen: isDeleteOpen, open: openDelete, close: closeDelete } = useModal();

  const [toDelete, setToDelete] = useState<Promotion | null>(null);
  const [search, setSearch] = useState('');

  const { handleDelete } = useCrud<Partial<Promotion>, Promotion>({
    queryKey: 'promotions',
    service: { remove: (id) => promotionService.remove(id) },
    messages: { deleted: 'Promotion deleted' },
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['promotions'],
    queryFn: () => promotionService.getAll(),
  });

  const promotions: Promotion[] =
    (data?.data as any)?.promotions ?? (data?.data as any)?.items ?? (Array.isArray(data?.data) ? data?.data : []);

  const filtered = search
    ? promotions.filter(
        (p) =>
          p.name.toLowerCase().includes(search.toLowerCase()) ||
          (p.code && p.code.toLowerCase().includes(search.toLowerCase()))
      )
    : promotions;

  const openPromotionForm = (promotion: Promotion | null) => {
    openAside(<PromotionForm promotion={promotion} onSuccess={closeAside} />, {
      title: promotion ? 'Edit Promotion' : 'New Promotion',
    });
  };

  const handleDeleteClick = (p: Promotion) => {
    setToDelete(p);
    openDelete();
  };

  const columns: ColumnDefinition<Promotion>[] = [
    {
      name: 'name',
      title: 'Name',
      value: (p) => (
        <div>
          <div className="font-medium text-sm text-gray-900 dark:text-white">{p.name}</div>
          {p.autoApply && <span className="text-xs text-brand-500 dark:text-brand-400">Auto-apply</span>}
        </div>
      ),
    },
    { name: 'code', title: 'Code', value: (p) => <span className="font-mono">{p.code || '—'}</span> },
    {
      name: 'type',
      title: 'Type',
      value: (p) => <Badge color={typeBadgeColors[p.type] ?? 'gray'}>{p.type.replace('_', ' ')}</Badge>,
    },
    {
      name: 'value',
      title: 'Value',
      value: (p) => <span className="font-medium text-gray-900 dark:text-white">{formatValue(p)}</span>,
    },
    {
      name: 'usage',
      title: 'Usage',
      value: (p) => (
        <span>
          {p.usageCount}
          {p.usageLimit ? ` / ${p.usageLimit}` : ''}
        </span>
      ),
    },
    { name: 'scope', title: 'Scope', value: (p) => p.appliesTo.replace('_', ' ') },
    { name: 'start', title: 'Start', value: (p) => formatDate(p.startsAt) },
    { name: 'expires', title: 'Expires', value: (p) => formatDate(p.expiresAt) },
    {
      name: 'status',
      title: 'Status',
      value: (p) => <Badge color={statusBadgeColors[p.status] ?? 'gray'}>{p.status}</Badge>,
    },
  ];

  return (
    <>
      <PageMeta
        title="Promotions"
        breadcrumbs={[{ label: 'Promotions' }]}
        action={
          <Button onClick={() => openPromotionForm(null)} icon={<PlusIcon className="h-5 w-5" />}>
            Add Promotion
          </Button>
        }
      />

      <TableCard
        columns={columns}
        data={filtered}
        loading={isLoading}
        error={error ? 'Failed to load promotions' : undefined}
        emptyMessage="No promotions found"
        searchable
        searchPlaceholder="Search by name or code..."
        onSearch={setSearch}
        onRowClick={openPromotionForm}
        rowActions={(p) => (
          <div className="flex items-center justify-end gap-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                openPromotionForm(p);
              }}
              className="text-brand-600 hover:text-brand-900 dark:text-brand-400 dark:hover:text-brand-300"
            >
              <PencilIcon className="h-4 w-4" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteClick(p);
              }}
              className="text-danger-600 hover:text-danger-900 dark:text-danger-400 dark:hover:text-danger-300"
            >
              <TrashIcon className="h-4 w-4" />
            </button>
          </div>
        )}
      />

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={isDeleteOpen}
        onClose={() => {
          closeDelete();
          setToDelete(null);
        }}
        onConfirm={async () => {
          if (!toDelete) return;
          await handleDelete(toDelete.id);
          setToDelete(null);
        }}
        title="Delete Promotion"
        description={`Are you sure you want to delete promotion "${toDelete?.name}"? This action cannot be undone.`}
        confirmLabel="Delete"
        successMessage="Promotion deleted"
      />
    </>
  );
};

export default Promotions;
